import { getImage } from './ugcImages';
import type { BackdropId } from './levels';

/**
 * Painted backdrops (generated art, client/public/art/<id>.jpg) that stand in for the drawn sky,
 * clouds and ridge on levels whose style names one. The image is drawn cover-fit in screen space
 * and drifts a little with the camera; until it has loaded, and on any level without one, the
 * drawn sky is used, so nothing ever waits on the network.
 */

interface BackdropDef {
  /**
   * Fraction of the image height where the sea or ground line sits. When set, the image is
   * placed so that line meets the level's waterline, so the painted sea and the game's water agree.
   */
  horizon: number | null;
}

const DEFS: Record<BackdropId, BackdropDef> = {
  meadow: { horizon: null },
  desert: { horizon: null },
  dusk: { horizon: null },
  dunes: { horizon: null },
  city: { horizon: null },
  cityday: { horizon: null },
  frost: { horizon: null },
  cave: { horizon: null },
  storm: { horizon: 0.6 },
  water: { horizon: 0.47 },
  sunset: { horizon: 0.51 },
  night: { horizon: 0.51 },
  facility: { horizon: null },
};

export function isBackdropId(id: unknown): id is BackdropId {
  return typeof id === 'string' && Object.prototype.hasOwnProperty.call(DEFS, id);
}

export function backdropUrl(id: BackdropId): string {
  const base = typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL ? import.meta.env.BASE_URL : '/';
  return `${base}art/${id}.jpg`;
}

/** Slow drift of the painting against the camera: far things barely move. */
const DRIFT_X = 0.04;
const DRIFT_Y = 0.03;
/** Extra size so there is room to drift without showing an edge. */
const OVERSCAN = 1.12;

export interface BackdropView {
  camX: number;
  camY: number;
  zoom: number;
  /** Canvas size in device px. */
  w: number;
  h: number;
  /** Screen y of the waterline (or where the ground would be). */
  baseY: number;
  /** Clock in ms, for the slow ambient sway. */
  now: number;
}

export class Backdrop {
  private readonly id: BackdropId | undefined;

  constructor(id: unknown) {
    this.id = isBackdropId(id) ? id : undefined;
    // Start loading right away so the art is ready by the first frame that can show it.
    if (this.id) getImage(backdropUrl(this.id));
  }

  /** Whether this level has a painted backdrop at all (loaded or not). */
  get wanted(): boolean {
    return this.id !== undefined;
  }

  /** Draws the backdrop; false means it is not available yet and the caller draws the sky instead. */
  draw(ctx: CanvasRenderingContext2D, v: BackdropView, levelW: number, levelH: number): boolean {
    if (!this.id) return false;
    const img = getImage(backdropUrl(this.id));
    if (!img || !img.naturalWidth) return false;
    const { w, h } = v;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const hz = DEFS[this.id].horizon;
    let s = Math.max(w / iw, h / ih) * OVERSCAN;
    if (hz !== null) {
      // Room above and below the horizon line for it to sit on the waterline.
      s = Math.max(s, v.baseY / (hz * ih), (h - v.baseY) / ((1 - hz) * ih));
    }
    const dw = iw * s;
    const dh = ih * s;
    const maxX = Math.max(0, (dw - w) / 2);
    // The painting sways a little on its own so a still camera is not a still picture.
    const sway = Math.sin(v.now / 14000) * maxX * 0.3;
    const px = Math.max(-maxX, Math.min(maxX, -(v.camX - levelW / 2) * v.zoom * DRIFT_X + sway));
    const x = (w - dw) / 2 + px;
    let y = hz !== null ? v.baseY - hz * dh : (h - dh) / 2 - (v.camY - levelH * 0.6) * v.zoom * DRIFT_Y;
    y = Math.max(h - dh, Math.min(0, y));
    ctx.drawImage(img, x, y, dw, dh);
    return true;
  }
}
